import { useState } from "react";
import {
  Button,
  IconButton,
  HideIcon,
  EyeIcon,
  DeleteIcon,
  Tooltip,
  ButtonGroup,
  Checkbox,
  Column,
  ConfirmationDialog,
  Text,
  useToast,
  type IconButtonProps,
} from "@hightouch/htui";
import {
  useAmpLibraryDeleteMutation,
  useAmpLibraryShareMutation,
  useAmpLibraryUsageQuery,
  type AmpLibraryFileFragment,
} from "src/graphql";
import type { FileMenuItem } from "src/pages/projects/library/canvas/file-menus";
import { useCanSetFileVisibility } from "src/pages/projects/library/files/use-can-set-file-visibility";

export function useFileActions({
  file,
  onDeleted,
  iconOnly = false,
  iconSize = "sm",
  iconShape,
}: {
  file: AmpLibraryFileFragment;
  iconOnly?: boolean;
  iconSize?: "sm" | "md";
  iconShape?: IconButtonProps["shape"];
  onDeleted: (id: string) => void;
}) {
  const [action, setAction] = useState<"share" | "delete">();
  const [deleteFiles, setDeleteFiles] = useState(false);
  const close = () => {
    setAction(undefined);
    setDeleteFiles(false);
  };
  const share = useAmpLibraryShareMutation();
  const remove = useAmpLibraryDeleteMutation();
  const isCanvas = file.type === "canvas";
  const {
    data: usage,
    isLoading,
    error,
  } = useAmpLibraryUsageQuery(
    { id: file.id },
    // A canvas is never placed on another canvas, so it has no usage to count.
    { enabled: action === "delete" && !isCanvas },
  );
  const { toast } = useToast();
  const makeShared = file.visibility === "private";
  const canChangeVisibility = useCanSetFileVisibility();
  const deleteLabel = isCanvas ? "Delete canvas" : "Delete file";
  const visibilityLabel = makeShared ? "Show" : "Hide";
  const deleteMenuItem: FileMenuItem = {
    label: deleteLabel,
    icon: DeleteIcon,
    isDanger: true,
    group: true,
    onSelect: () => setAction("delete"),
  };
  // Staff-only, so every menu closes on it under a rule.
  const visibilityMenuItem: FileMenuItem | undefined = canChangeVisibility
    ? {
        label: makeShared ? "Show to workspace" : "Hide (internal)",
        icon: makeShared ? EyeIcon : HideIcon,
        isInternal: true,
        group: true,
        onSelect: () => setAction("share"),
      }
    : undefined;
  const buttons = (
    <ButtonGroup>
      {iconOnly ? (
        <>
          {canChangeVisibility && (
            <Tooltip message={visibilityLabel}>
              <IconButton
                aria-label={visibilityLabel}
                icon={makeShared ? EyeIcon : HideIcon}
                variant="tertiary"
                size={iconSize}
                shape={iconShape}
                onClick={() => setAction("share")}
              />
            </Tooltip>
          )}
          <Tooltip message={deleteLabel}>
            <IconButton
              aria-label={deleteLabel}
              icon={DeleteIcon}
              variant="tertiary"
              size={iconSize}
              shape={iconShape}
              onClick={() => setAction("delete")}
            />
          </Tooltip>
        </>
      ) : (
        <>
          {canChangeVisibility && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setAction("share")}
            >
              {visibilityLabel}
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setAction("delete")}
          >
            Delete
          </Button>
        </>
      )}
    </ButtonGroup>
  );
  const confirmation = (
    <ConfirmationDialog
      isOpen={Boolean(action)}
      variant={action === "delete" ? "danger" : "warning"}
      title={
        action === "delete"
          ? isCanvas && deleteFiles
            ? "Delete canvas and files"
            : deleteLabel
          : `${visibilityLabel} ${isCanvas ? "canvas" : "file"}`
      }
      confirmButtonText={action === "delete" ? "Delete" : visibilityLabel}
      isConfirmButtonDisabled={
        share.isLoading ||
        remove.isLoading ||
        (action === "delete" && (isLoading || Boolean(error)))
      }
      onClose={close}
      onConfirm={async () => {
        try {
          if (action === "delete") {
            await remove.mutateAsync({ id: file.id, deleteFiles });
            onDeleted(file.id);
          } else {
            await share.mutateAsync({
              id: file.id,
              visibility: makeShared ? "workspace" : "private",
            });
          }
          close();
        } catch (error) {
          toast({
            id: "library-action",
            variant: "error",
            title:
              error instanceof Error ? error.message : "Could not update file",
          });
        }
      }}
    >
      <Column gap={4}>
        <Text>
          {action === "delete"
            ? isCanvas
              ? deleteFiles
                ? "Files on this canvas will also be deleted from your Library and any other canvases where they’re used."
                : "Files on this canvas will stay in your Library."
              : error
                ? "Could not check canvas usage. Close this dialog and try again."
                : `This file will be removed from ${usage?.ampFileUsage.count ?? 0} canvases. You can undo this deletion after it finishes.`
            : makeShared
              ? file.type === "canvas"
                ? "Everyone in this workspace will be able to view and edit this canvas and all files in it."
                : "Everyone in this workspace will be able to view and edit this file."
              : "Only Hightouch staff will be able to access this file. Files on a shared canvas must remain shared."}
        </Text>
        {action === "delete" && isCanvas && (
          <Checkbox
            label="Also delete files from Library"
            isChecked={deleteFiles}
            onChange={(event) => setDeleteFiles(event.target.checked)}
          />
        )}
      </Column>
    </ConfirmationDialog>
  );
  return {
    buttons,
    confirmation,
    isConfirming: Boolean(action),
    requestDelete: () => setAction("delete"),
    deleteMenuItem,
    visibilityMenuItem,
  };
}
